/* eslint-disable react/prop-types */
import React, { useRef } from 'react';
import {
	Box,
	Button,
	Chip,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Typography,
	useTheme,
	useMediaQuery,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

interface FileUploaderProps {
	files: File[];
	onChange: (files: File[]) => void;
	accept?: string;
	maxFiles?: number;
	maxSizeMB?: number;
	disabled?: boolean;
	label?: string;
}

const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return '0 Bytes';
	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const isValidFileType = (file: File, accept?: string): boolean => {
	if (!accept) return true;

	const acceptedTypes = accept.split(',').map((type) => type.trim());
	return acceptedTypes.some((type) => {
		if (type.startsWith('.')) {
			return file.name.toLowerCase().endsWith(type.toLowerCase());
		}
		if (type.includes('/*')) {
			const baseType = type.split('/')[0];
			return file.type.startsWith(baseType);
		}
		return file.type === type;
	});
};

export const FileUploader: React.FC<FileUploaderProps> = ({
	files,
	onChange,
	accept = '.pdf,.jpg,.jpeg,.png',
	maxFiles = 10,
	maxSizeMB = 10,
	disabled = false,
	label = 'Archivos adjuntos',
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = Array.from(event.target.files || []);

		// Validar cantidad de archivos
		if (files.length + selectedFiles.length > maxFiles) {
			alert(`Solo se pueden subir hasta ${maxFiles} archivos`);
			return;
		}

		// Validar cada archivo
		const validFiles: File[] = [];
		for (const file of selectedFiles) {
			// Validar tipo
			if (!isValidFileType(file, accept)) {
				alert(`El archivo ${file.name} no es del tipo permitido. Tipos permitidos: ${accept}`);
				continue;
			}

			// Validar tamaño
			const fileSizeMB = file.size / (1024 * 1024);
			if (fileSizeMB > maxSizeMB) {
				alert(`El archivo ${file.name} excede el tamaño máximo de ${maxSizeMB}MB`);
				continue;
			}

			validFiles.push(file);
		}

		if (validFiles.length > 0) {
			onChange([...files, ...validFiles]);
		}

		// Limpiar el input para permitir seleccionar el mismo archivo nuevamente
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleRemoveFile = (index: number) => {
		const newFiles = files.filter((_, i) => i !== index);
		onChange(newFiles);
	};

	const handleButtonClick = () => {
		fileInputRef.current?.click();
	};

	return (
		<Box>
			<Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
				{label}
			</Typography>
			<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
				Tipos permitidos: {accept} | Máximo: {maxFiles} archivos | Tamaño máximo por archivo: {maxSizeMB}MB
			</Typography>

			<input
				ref={fileInputRef}
				type="file"
				accept={accept}
				multiple
				style={{ display: 'none' }}
				onChange={handleFileSelect}
				disabled={disabled}
			/>

			<Button
				variant="outlined"
				startIcon={<CloudUploadIcon />}
				onClick={handleButtonClick}
				disabled={disabled || files.length >= maxFiles}
				fullWidth={isMobile}
				sx={{ mb: 2 }}
			>
				Seleccionar archivos
			</Button>

			{files.length > 0 && (
				<List>
					{files.map((file, index) => (
						<ListItem
							key={index}
							secondaryAction={
								<IconButton edge="end" aria-label="delete" onClick={() => handleRemoveFile(index)} disabled={disabled}>
									<DeleteIcon />
								</IconButton>
							}
							sx={{
								border: '1px solid',
								borderColor: 'divider',
								borderRadius: 1,
								mb: 1,
							}}
						>
							<InsertDriveFileIcon sx={{ mr: 1, color: 'text.secondary' }} />
							<ListItemText
								primary={file.name}
								secondary={
									<Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
										<Chip label={formatFileSize(file.size)} size="small" variant="outlined" />
										<Chip label={file.type || 'Sin tipo'} size="small" variant="outlined" />
									</Box>
								}
							/>
						</ListItem>
					))}
				</List>
			)}
		</Box>
	);
};
